// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** CapabilityHost — the host side of CONTRACT:C3-CAPABILITY-PLUGIN.1.0.
 *
 *  Responsibilities:
 *   - Assemble a per-capability HostContext (engine, register{Layer,Panel,Command,
 *     ContextMenu}, data.subscribe/publish, tokens, policy.decide) — the ONLY
 *     surface a capability may touch (light coupling).
 *   - Manage lifecycle: activate() / deactivate(); lazy-load via an async loader.
 *   - Policy-gate: run a capability's DECLARED permissions through policy.decide
 *     before activating; deny ⇒ ErrPermissionDenied, register nothing.
 *   - Crash isolation: a throw in activate() ⇒ ErrCapabilityActivation (no
 *     contributions registered); a throw in any capability callback ⇒
 *     ErrCapabilityCrash (tear down ONLY that capability; host + others stay live).
 *   - Disposable contributions: deactivate / crash removes every contribution. */

import type {
  RenderEngine,
  LayerHandle,
  DesignTokens,
  CanonicalEnvelope,
  EntityFilter,
  LayerSpec,
  PanelSpec,
  CommandSpec,
  ContextMenuItem,
  Disposable,
  PolicyRequest,
  PolicyDecision,
  CapabilityManifest,
  ActorClass,
} from "../../contracts-ts/src/index.ts";
import type { HostContext, Capability } from "./types.ts";
import type {
  CapabilityRegistration,
  LazyCapabilityRegistration,
  DeclaredPermission,
  HostLogger,
  CapabilityState,
  CapabilityStatus,
} from "./types.ts";
import { ContributionRegistry, type ContributionKind } from "./registry.ts";
import { gate } from "./policyGate.ts";
import {
  manifestAllowsKind,
  manifestAllowsCommand,
  validateInput,
} from "./manifestGate.ts";
import {
  CapabilityError,
  ErrCapabilityActivation,
  ErrCapabilityCrash,
  ErrManifestOverreach,
  ErrInputInvalid,
  ErrHumanGateRequired,
  ErrPermissionDenied,
  ErrActorRefused,
} from "./errors.ts";

/** A host-level confirmation hook the caller MUST satisfy for a `risk:"irreversible"`
 *  command — the T-IRREVERSIBLE human gate enforced OUTSIDE any model (AOA §2). It is
 *  passed to `dispatchCommand` and returns whether a HUMAN explicitly confirmed the
 *  action. An ai-agent / npe actor cannot self-confirm: the host requires the gate
 *  regardless of the model's output, so no prompt can route around it. */
export type HumanGateConfirm = (ctx: {
  commandId: string;
  /** The actor attempting the dispatch (from the request subject). */
  actorClass: ActorClass | undefined;
}) => boolean | Promise<boolean>;

/** Options for a host-mediated command dispatch (the automation / palette entrypoint). */
export interface DispatchOptions {
  /** The machine-supplied payload, validated against the command's typed `input`. */
  args?: unknown;
  /** The actor invoking the command (its class drives the irreversible human gate). */
  actorClass?: ActorClass;
  /** The host confirmation hook for `risk:"irreversible"` commands. */
  confirmHumanGate?: HumanGateConfirm;
}

/** A registered panel paired with its owning capability — the unit `CapabilityHost.panels()`
 *  yields so the host (`<PanelHost>`) can render every contributed `PanelSpec` generically.
 *  The `capabilityId` scopes the host-owned dock key/teardown; the `spec.mount` (capability-
 *  owned) renders the panel's own content into the host-owned container. */
export interface PanelContribution {
  capabilityId: string;
  spec: PanelSpec;
}

/** The host-backing services the engine/bus/tokens/policy are sourced from.
 *  A conforming host (tak-tdf or blindpipe) supplies its OWN implementations;
 *  the HostContext surface a capability sees stays identical. */
export interface HostServices {
  engine: RenderEngine;
  tokens: DesignTokens;
  policy: { decide(req: PolicyRequest): Promise<PolicyDecision> };
  data: {
    subscribe(
      filter: EntityFilter,
      cb: (env: CanonicalEnvelope) => void,
    ): Disposable;
    publish(env: CanonicalEnvelope): Promise<void>;
  };
  /** Optional structured log sink for isolated crashes (defaults to console). */
  logger?: HostLogger;
}

interface ManagedCapability {
  id: string;
  status: CapabilityStatus;
  errorCode?: string;
  /** Resolved capability instance once loaded/activated. */
  instance?: Capability;
  /** Loader for a lazy registration (undefined for eager registrations). */
  load?: LazyCapabilityRegistration["load"];
  declares?: DeclaredPermission[];
  /** The optional 0.2a capability manifest the host gates contributions on. ABSENT
   *  ⇒ legacy-permissive (every register* is ungated). */
  manifest?: CapabilityManifest;
  /** Obligations returned by policy on activation; MUST be honored. */
  obligations: PolicyDecision["obligations"];
}

/** Fold the manifest's declared `permissions` into the gated `declares` set so the
 *  manifest's required permissions ARE policy-gated before activation (AOA §3a). PURE
 *  union (declares first, then manifest.permissions); legacy-permissive when both
 *  absent. */
function combineDeclares(
  declares: DeclaredPermission[] | undefined,
  manifest: CapabilityManifest | undefined,
): DeclaredPermission[] | undefined {
  // GUARD the spread: a present-but-non-array `permissions` (reachable only via an
  // `as`-cast / runtime-parsed manifest, since the type says PolicyRequest[]) must NOT
  // throw a synchronous TypeError out of register(). We drop the malformed value here;
  // the malformed manifest is independently DENIED at activate() (see manifestIsMalformed)
  // so this is never a silent fail-open — it is a defensive non-throw.
  const fromManifest = Array.isArray(manifest?.permissions)
    ? (manifest!.permissions as DeclaredPermission[])
    : [];
  if ((declares?.length ?? 0) === 0 && fromManifest.length === 0) return declares;
  return [...(declares ?? []), ...fromManifest];
}

/** FAIL-CLOSED manifest validation (AOA §1 "unparseable manifest ⇒ deny"). A manifest
 *  whose structural fields are present-but-malformed (a non-array `contributes`,
 *  `permissions`, or `commandActors` that is not an object, or a `commandActors[id]`
 *  that is not an array) is UNPARSEABLE — the host DENIES activation rather than
 *  silently dropping the broken constraint and defaulting open. Returns a reason string
 *  when malformed, or undefined when the manifest is well-formed (or absent). PURE.
 *  Reachable only via casts / runtime-parsed manifests (the TS type forbids these);
 *  this is the runtime fail-closed backstop. */
function manifestMalformedReason(
  manifest: CapabilityManifest | undefined,
): string | undefined {
  if (!manifest) return undefined;
  if (!Array.isArray(manifest.contributes)) {
    return "manifest.contributes is not an array (unparseable ⇒ deny)";
  }
  const perms = (manifest as { permissions?: unknown }).permissions;
  if (perms !== undefined && !Array.isArray(perms)) {
    return "manifest.permissions is not an array (unparseable ⇒ deny)";
  }
  const ca = (manifest as { commandActors?: unknown }).commandActors;
  if (ca !== undefined) {
    if (typeof ca !== "object" || ca === null || Array.isArray(ca)) {
      return "manifest.commandActors is not an object (unparseable ⇒ deny)";
    }
    for (const [id, list] of Object.entries(ca as Record<string, unknown>)) {
      if (!Array.isArray(list)) {
        return `manifest.commandActors["${id}"] is not an array (unparseable ⇒ deny)`;
      }
    }
  }
  return undefined;
}

/** A refused contribution returns this inert handle: disposing it is a no-op (nothing
 *  was registered). Shared so a refusal never allocates. */
const NOOP_DISPOSABLE: Disposable = { dispose() {} };

/** The RiskTier values that are NOT human-gated. Any `risk` value outside this set —
 *  including a malformed/unrecognized one supplied via an `as any` / runtime-built
 *  CommandSpec — is treated as IRREVERSIBLE (fail-closed: an unparseable risk tier
 *  triggers the human gate rather than silently skipping it). AOA §1/§2. */
const NON_GATED_RISK: ReadonlySet<string> = new Set(["read", "reversible", "external"]);

/** Does this command's `risk` require the mandatory human gate? TRUE for the literal
 *  "irreversible" AND for any present-but-unrecognized risk value (fail-closed). FALSE
 *  only when risk is absent (untyped/legacy) or an explicitly NON-gated known tier. PURE. */
function requiresHumanGate(risk: unknown): boolean {
  if (risk === undefined || risk === null) return false; // untyped ⇒ legacy-permissive
  if (typeof risk !== "string") return true; // malformed truthy ⇒ fail-closed
  return !NON_GATED_RISK.has(risk); // "irreversible" OR any unknown string ⇒ gated
}

export class CapabilityHost {
  private readonly registry = new ContributionRegistry();
  private readonly managed = new Map<string, ManagedCapability>();
  private readonly services: HostServices;
  private readonly logger: HostLogger;

  constructor(services: HostServices) {
    this.services = services;
    this.logger =
      services.logger ??
      {
        error(event) {
          // Default sink: log, never throw past the host.
          // eslint-disable-next-line no-console
          console.error(
            `[capability-host] ${event.code} ${event.capabilityId}: ${event.message}`,
          );
        },
      };
  }

  /** Register an eagerly-available capability (the module is already in hand). */
  register(reg: CapabilityRegistration): void {
    const id = reg.capability.id;
    this.assertUnregistered(id);
    this.managed.set(id, {
      id,
      status: "registered",
      instance: reg.capability,
      declares: combineDeclares(reg.declares, reg.manifest),
      manifest: reg.manifest,
      obligations: [],
    });
  }

  /** Register a lazy / independently-bundled capability; the module is fetched
   *  on activate(). No rebuild needed to add/remove a capability. */
  registerLazy(reg: LazyCapabilityRegistration): void {
    this.assertUnregistered(reg.id);
    this.managed.set(reg.id, {
      id: reg.id,
      status: "registered",
      load: reg.load,
      declares: combineDeclares(reg.declares, reg.manifest),
      manifest: reg.manifest,
      obligations: [],
    });
  }

  /** Activate a registered capability:
   *   1. policy-gate declared permissions (deny ⇒ ErrPermissionDenied),
   *   2. lazy-load the module if needed,
   *   3. assemble its HostContext and call activate(),
   *   4. a throw ⇒ ErrCapabilityActivation, contributions rolled back.
   *  Throws the relevant CapabilityError; the host itself always stays live. */
  async activate(id: string): Promise<void> {
    const mc = this.managed.get(id);
    if (!mc) {
      throw new ErrCapabilityActivation(id, `capability "${id}" is not registered`);
    }
    if (mc.status === "active") return;

    // 0. FAIL-CLOSED manifest validation (AOA §1). An unparseable manifest (a malformed
    //    contributes / permissions / commandActors reachable only via casts) is DENIED
    //    here — never registered, never default-open. This runs BEFORE the policy gate so
    //    a broken manifest can never reach activation.
    const malformed = manifestMalformedReason(mc.manifest);
    if (malformed) {
      const e = new ErrPermissionDenied(id, `manifest unparseable (fail-closed): ${malformed}`);
      mc.status = "denied";
      mc.errorCode = e.code;
      this.logError(e, id);
      throw e;
    }

    // 1. Policy gate (before loading anything). Rejects with ErrPermissionDenied.
    let obligations: PolicyDecision["obligations"];
    try {
      const result = await gate(id, this.services.policy, mc.declares);
      obligations = result.obligations;
    } catch (err) {
      mc.status = "denied";
      mc.errorCode = err instanceof CapabilityError ? err.code : "PERMISSION_DENIED";
      this.logError(err, id);
      throw err;
    }
    mc.obligations = obligations;

    // 2. Lazy-load the module if needed.
    if (!mc.instance) {
      if (!mc.load) {
        const e = new ErrCapabilityActivation(id, `no instance and no loader`);
        mc.status = "failed";
        mc.errorCode = e.code;
        throw e;
      }
      try {
        const loaded = await mc.load();
        mc.instance = isModule(loaded) ? loaded.default : loaded;
      } catch (cause) {
        const e = new ErrCapabilityActivation(id, cause);
        mc.status = "failed";
        mc.errorCode = e.code;
        this.logError(e, id);
        throw e;
      }
    }

    // 3. Assemble HostContext + activate, with rollback on throw.
    const ctx = this.makeContext(id);
    try {
      await mc.instance!.activate(ctx);
      mc.status = "active";
    } catch (cause) {
      // Roll back any contributions made before the throw: register NONE.
      this.registry.disposeAll(id);
      const e = new ErrCapabilityActivation(id, cause);
      mc.status = "failed";
      mc.errorCode = e.code;
      this.logError(e, id);
      throw e;
    }
  }

  /** Deactivate a capability: call its deactivate() (isolated) and tear down
   *  EVERY contribution it made — leaving no orphan layers/panels/commands. */
  async deactivate(id: string): Promise<void> {
    const mc = this.managed.get(id);
    if (!mc) return;
    if (mc.instance?.deactivate) {
      try {
        await mc.instance.deactivate();
      } catch (cause) {
        // A throwing teardown is isolated; cleanup still proceeds.
        this.logError(new ErrCapabilityCrash(id, cause), id);
      }
    }
    this.registry.disposeAll(id);
    mc.status = "deactivated";
  }

  /** Read-only snapshot of a capability's host-side state. */
  state(id: string): CapabilityState | undefined {
    const mc = this.managed.get(id);
    if (!mc) return undefined;
    return {
      id: mc.id,
      status: mc.status,
      errorCode: mc.errorCode,
      contributionCount: this.registry.list(id).length,
    };
  }

  /** Ids of every registered capability. */
  capabilities(): string[] {
    return [...this.managed.keys()];
  }

  /** Live contributions for a capability (for the host's layout/palette/engine). */
  contributions(id: string) {
    return this.registry.list(id);
  }

  /** CROSS-CAPABILITY PANEL ENUMERATOR (Seam B — the panel-mount fix). Folds
   *  `registry.listKind(id,'panel')` over `capabilities()` so the host can render
   *  EVERY registered `PanelSpec` generically — the missing read-back half that lets
   *  a third-party add-in put a panel on screen through the PUBLIC C3 API alone
   *  (`ctx.registerPanel(spec)`), with no per-capability host wiring. Each entry pairs
   *  the spec with its owning `capabilityId` so the host can scope teardown/keys and
   *  the spec's `mount` closes over the capability's own renderer.
   *
   *  Read-only (a snapshot at call time; the registry returns defensive copies). It does
   *  NOT change `registerPanel`/`gateKind` — a panel still only lands here if the manifest
   *  permitted it (a panel-less manifest's `registerPanel` was already refused as a no-op,
   *  so it never enters the registry and is never enumerated here). Insertion order within
   *  a capability is preserved; capabilities are returned in registration order. */
  panels(): PanelContribution[] {
    const out: PanelContribution[] = [];
    for (const capabilityId of this.capabilities()) {
      for (const rec of this.registry.listKind(capabilityId, "panel")) {
        out.push({ capabilityId, spec: rec.spec as PanelSpec });
      }
    }
    return out;
  }

  /** Obligations policy attached to a capability's activation; MUST be honored. */
  obligations(id: string): PolicyDecision["obligations"] {
    return this.managed.get(id)?.obligations ?? [];
  }

  /** HOST-MEDIATED COMMAND DISPATCH (AOA §2/§3b) — the single entrypoint a palette /
   *  automation API / ai-agent uses to invoke a registered command. It enforces, in
   *  order, the CLOSED gate that applies ONLY to opt-in (typed/risk-declared) commands:
   *
   *   1. INVOCATION-TIME ACTOR GATE — if the owning capability's manifest constrains this
   *      command id via `commandActors[id]`, the presented `actorClass` MUST be in that
   *      allow-list; otherwise ErrActorRefused (the dispatch half of the actor gate, paired
   *      with the registration-time over-reach refusal).
   *   2. TYPED-INPUT VALIDATION — if the command declares a typed `input` schema,
   *      VALIDATE the machine-supplied payload BEFORE run(); invalid ⇒ ErrInputInvalid,
   *      run() is NEVER called.
   *   3. T-IRREVERSIBLE HUMAN GATE — if `risk:"irreversible"` (OR an unrecognized risk
   *      value, which fails closed to gated), the caller MUST satisfy the host confirmation
   *      hook (`opts.confirmHumanGate`) AND the actor must be EXPLICITLY a human; an
   *      ai-agent / npe / an OMITTED actorClass (fail-closed) / a missing/false confirmation
   *      ⇒ ErrHumanGateRequired, run() is NEVER called. The gate lives HERE, outside any model.
   *
   *  LEGACY-PERMISSIVE: a command that declares neither `input` nor `risk` (the existing
   *  `{ id, title, run }` shape) is dispatched UNGATED — back-compat by construction.
   *  FAIL-CLOSED: an unparseable typed schema ⇒ ErrInputInvalid; an unrecognized risk tier
   *  ⇒ human-gated; an omitted actorClass on an irreversible command ⇒ denied (never open).
   *
   *  The command's own `run` is already crash-isolated by makeContext; this method
   *  awaits it so a caller sees completion (or the gate rejection). */
  async dispatchCommand(
    capabilityId: string,
    commandId: string,
    opts: DispatchOptions = {},
  ): Promise<void> {
    const record = this.registry
      .listKind(capabilityId, "command")
      .find((c) => (c.spec as CommandSpec).id === commandId);
    if (!record) {
      // Glomar-safe: an unknown/never-registered command is indistinguishable from a
      // refused one — a plain "not found", no internals leaked.
      throw new ErrInputInvalid(commandId, "no such registered command");
    }
    const cmd = record.spec as CommandSpec;
    const manifest = this.managed.get(capabilityId)?.manifest;

    // 1. INVOCATION-TIME ACTOR GATE (opt-in: only when the manifest constrains this id via
    //    commandActors). The registration-time gate (manifestGate) blocks a CommandSpec
    //    that WIDENS the manifest allow-list; THIS closes the other half — the actor CLASS
    //    presented at dispatch must itself be in the manifest's allow-list for this command
    //    id. A command the manifest meant for humans-only cannot be self-dispatched by an
    //    ai-agent/npe even if its CommandSpec declared no actors. FAIL-CLOSED on a malformed
    //    allow-list (manifestMalformedReason already denied activation, but guard anyway).
    if (manifest && opts.actorClass !== undefined) {
      const ca = manifest.commandActors;
      if (ca && Object.prototype.hasOwnProperty.call(ca, commandId)) {
        const allowed = ca[commandId];
        if (!Array.isArray(allowed) || !allowed.includes(opts.actorClass)) {
          throw new ErrActorRefused(
            commandId,
            `actor class "${opts.actorClass}" is not in the manifest allow-list ` +
              `(${Array.isArray(allowed) ? `[${allowed.join(", ")}]` : "malformed ⇒ deny"})`,
          );
        }
      }
    }

    // 2. Typed-input validation (opt-in: only when the command declares `input`).
    if (cmd.input !== undefined && cmd.input !== null) {
      const result = validateInput(cmd.input, opts.args);
      if (!result.valid) {
        throw new ErrInputInvalid(commandId, result.error);
      }
    }

    // 3. The T-IRREVERSIBLE human gate (opt-in: `risk:"irreversible"` OR any unrecognized
    //    risk value, which fails closed to gated — requiresHumanGate()).
    if (requiresHumanGate(cmd.risk)) {
      // An ai-agent / npe can NEVER self-dispatch an irreversible command — only a human,
      // and only with an explicit satisfied confirmation hook. An OMITTED actorClass is
      // treated as NOT-human (fail-closed): an unstamped automation caller cannot route
      // around the actor-nature bar by simply not declaring its class (AOA §2 — the gate
      // no prompt can bypass).
      if (opts.actorClass !== "human") {
        throw new ErrHumanGateRequired(
          commandId,
          opts.actorClass === undefined
            ? "actorClass omitted; an irreversible action requires an explicit human actor (fail-closed)"
            : `actor class "${opts.actorClass}" cannot self-confirm an irreversible action`,
        );
      }
      if (typeof opts.confirmHumanGate !== "function") {
        throw new ErrHumanGateRequired(
          commandId,
          "no human confirmation hook supplied",
        );
      }
      let confirmed = false;
      try {
        confirmed = await opts.confirmHumanGate({
          commandId,
          actorClass: opts.actorClass,
        });
      } catch (cause) {
        // Fail-closed: a throwing confirmation hook denies (never default-open).
        throw new ErrHumanGateRequired(commandId, `confirmation hook failed: ${String(cause)}`);
      }
      if (confirmed !== true) {
        throw new ErrHumanGateRequired(commandId, "human confirmation was not granted");
      }
    }

    // Dispatch. `run` is the makeContext-wrapped, crash-isolated verb.
    await cmd.run(opts.args);
  }

  // --- internals -------------------------------------------------------------

  /** Build the per-capability HostContext. register* records into the registry;
   *  command/menu/subscribe verbs are wrapped so a throw is isolated as an
   *  ErrCapabilityCrash that tears down ONLY this capability. */
  private makeContext(id: string): HostContext {
    const self = this;
    const manifest = this.managed.get(id)?.manifest;
    const isolate = <A extends unknown[]>(
      fn: (...args: A) => unknown,
    ): ((...args: A) => void) => {
      return (...args: A) => {
        try {
          const r = fn(...args);
          if (r instanceof Promise) {
            r.catch((cause) => self.crash(id, cause));
          }
        } catch (cause) {
          self.crash(id, cause);
        }
      };
    };

    /** Manifest-gate a non-command contribution kind. ABSENT manifest ⇒ legacy-
     *  permissive (always allowed). Over-reach ⇒ refuse (no-op Disposable) + log
     *  ErrManifestOverreach; the capability stays live. */
    const gateKind = (kind: ContributionKind): boolean => {
      if (!manifest) return true;
      const verdict = manifestAllowsKind(manifest, kind);
      if (!verdict.allowed) {
        self.logError(new ErrManifestOverreach(id, verdict.reason), id);
        return false;
      }
      return true;
    };

    return {
      engine: this.services.engine,

      registerLayer(spec: LayerSpec): Disposable {
        if (!gateKind("layer")) return NOOP_DISPOSABLE;
        const rec = self.registry.add(id, "layer", spec);
        // HOST-MEDIATED engine layer (the contribute pattern, Increment 3 / Move 2): the
        // host creates the GPU layer on the capability's behalf, so a capability never
        // touches the engine's write surface (CapabilityEngine omits addLayer/removeLayer).
        // Idempotent on re-activation: if the layer already exists, the existing one stands.
        let handle: LayerHandle | null = null;
        try {
          handle = self.services.engine.addLayer(spec);
        } catch {
          handle = null;
        }
        return {
          dispose(): void {
            if (handle) {
              try {
                self.services.engine.removeLayer(handle);
              } catch {
                // already gone (host teardown / re-activation race) — best-effort
              }
            }
            rec.dispose();
          },
        };
      },
      registerPanel(panel: PanelSpec): Disposable {
        if (!gateKind("panel")) return NOOP_DISPOSABLE;
        return self.registry.add(id, "panel", panel);
      },
      registerCommand(cmd: CommandSpec): Disposable {
        // MANIFEST GATE: refuse a command whose kind/id/actors over-reach the manifest
        // (register nothing for it) + log; the capability stays live. Legacy-permissive
        // when no manifest is supplied.
        if (manifest) {
          const verdict = manifestAllowsCommand(manifest, cmd.id, cmd.actors);
          if (!verdict.allowed) {
            self.logError(new ErrManifestOverreach(id, verdict.reason), id);
            return NOOP_DISPOSABLE;
          }
        }
        // Wrap run so a runtime throw is crash-isolated, not propagated.
        const safe: CommandSpec = { ...cmd, run: isolate(cmd.run) };
        return self.registry.add(id, "command", safe);
      },
      registerContextMenu(item: ContextMenuItem): Disposable {
        if (!gateKind("context-menu")) return NOOP_DISPOSABLE;
        const safe: ContextMenuItem = { ...item, run: isolate(item.run) };
        return self.registry.add(id, "context-menu", safe);
      },

      data: {
        subscribe(
          filter: EntityFilter,
          cb: (env: CanonicalEnvelope) => void,
        ): Disposable {
          // Isolate the subscriber callback too: a throw inside it must not
          // take down the bus or the host.
          return self.services.data.subscribe(filter, isolate(cb));
        },
        publish(env: CanonicalEnvelope): Promise<void> {
          return self.services.data.publish(env);
        },
      },

      tokens: this.services.tokens,

      policy: {
        decide(req: PolicyRequest): Promise<PolicyDecision> {
          return self.services.policy.decide(req);
        },
      },
    };
  }

  /** Crash isolation: tear down only this capability's contributions, mark it
   *  crashed, log the ErrCapabilityCrash — and let the host keep running. */
  private crash(id: string, cause: unknown): void {
    const mc = this.managed.get(id);
    const err = new ErrCapabilityCrash(id, cause);
    this.registry.disposeAll(id);
    const inst = mc?.instance;
    if (inst?.deactivate) {
      try {
        const r = inst.deactivate();
        if (r instanceof Promise) r.catch(() => {});
      } catch {
        // deactivate during crash is best-effort
      }
    }
    if (mc) {
      mc.status = "crashed";
      mc.errorCode = err.code;
    }
    this.logError(err, id);
  }

  private assertUnregistered(id: string): void {
    if (this.managed.has(id)) {
      throw new ErrCapabilityActivation(id, `capability "${id}" already registered`);
    }
  }

  private logError(err: unknown, id: string): void {
    const code = err instanceof CapabilityError ? err.code : "CAPABILITY_CRASH";
    const message = err instanceof Error ? err.message : String(err);
    const cause = err instanceof CapabilityError ? err.cause : err;
    this.logger.error({ capabilityId: id, code, message, cause });
  }
}

function isModule(x: unknown): x is { default: Capability } {
  return (
    typeof x === "object" &&
    x !== null &&
    "default" in x &&
    typeof (x as { default: unknown }).default === "object"
  );
}
